import { useState, useCallback, Dispatch, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { upload } from 'src/helpers/upLoadHelper';
import {
  EMAIL_REGEX,
  PHONE_REGEX,
  USER_ROLE_NAMES,
  UserProfileSettings,
  ValidationErrors,
} from 'src/pages/Settings/types';
import { getUserProfileDetails, updateUserProfile } from 'src/services/client';
import { useStagedImagePreview } from 'src/components/ProfileImageField/useStagedImagePreview';
import { toast } from 'react-toastify';
import { updateAuthenticatedUser } from 'src/store/actions/auth';

export interface UseAccountSettingsReturn {
  settings: UserProfileSettings | null;
  errors: ValidationErrors;
  saveClicked: boolean;
  calendlyAPIKeyHidden: boolean;
  showCalendarFields: boolean;
  user: any;
  isLoading: boolean;
  isDirty: boolean;
  stagedPreviewUrl: string | null;
  hasStagedImage: boolean;
  updateField: (field: string, value: string | File[]) => void;
  handleSubmit: () => Promise<boolean>;
  unlinkCalendly: () => Promise<boolean>;
  clearSaveState: () => void;
  toggleCalendlyKeyVisibility: () => void;
}

export const useAccountSettings = (): UseAccountSettingsReturn => {
  const dispatch: Dispatch<any> = useDispatch();
  const user: any = useSelector(selectAuthenticatedUser);

  const [settings, setSettings] = useState<UserProfileSettings | null>(null);
  const [savedSettings, setSavedSettings] =
    useState<UserProfileSettings | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saveClicked, setSaveClicked] = useState(false);
  const [calendlyAPIKeyHidden, setCalendlyAPIKeyHidden] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const userRoleName: any = user?.roles?.[0]?.role?.name;

  const showCalendarFields: boolean =
    userRoleName === USER_ROLE_NAMES.PROPERTY_DEVELOPER ||
    userRoleName === USER_ROLE_NAMES.SERVICE_PROVIDER;

  const fetchSettings = async () => {
    if (!user?.id) return;

    try {
      const response: any = await getUserProfileDetails(user.id);
      if (response.status === 200 || response.ok) {
        const data = await response.json();

        setSettings(data);
        setSavedSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch account settings', error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [user?.id]);

  const clearSaveState = useCallback(() => {
    setSaveClicked(false);
  }, []);

  const toggleCalendlyKeyVisibility = useCallback(() => {
    setCalendlyAPIKeyHidden((prev: boolean) => !prev);
  }, []);

  const updateField = useCallback(
    (field: string, value: string | File[]) => {
      setSettings((prev: UserProfileSettings) =>
        prev
          ? {
              ...prev,
              [field]: value,
            }
          : null
      );
    },
    [dispatch]
  );

  const { stagedPreviewUrl, hasStagedImage } = useStagedImagePreview(
    settings?.selectedProfileImage
  );

  const nameOrPhoneChanged: boolean =
    savedSettings !== null &&
    settings !== null &&
    (settings.firstname !== savedSettings.firstname ||
      settings.lastname !== savedSettings.lastname ||
      settings.phone !== savedSettings.phone);

  const isDirty: boolean = hasStagedImage || nameOrPhoneChanged;

  const formatPhoneNumber = (phone: string): string => {
    const cleaned: string = ('' + phone).replace(/\D/g, '');

    if (cleaned.length === 10) {
      const match: RegExpMatchArray = cleaned.match(
        /^(?<areaCode>\d{3})(?<prefix>\d{3})(?<lineNumber>\d{4})$/
      );

      if (match?.groups) {
        const { areaCode, prefix, lineNumber } = match.groups;
        return `${areaCode}-${prefix}-${lineNumber}`;
      }
    }

    return cleaned;
  };

  const validateForm = (): ValidationErrors => {
    const validationErrors: ValidationErrors = {};

    if (!settings?.firstname?.trim()) {
      validationErrors.firstname = 'Please Enter First Name !';
      validationErrors.errors = true;
    }

    if (!settings?.lastname?.trim()) {
      validationErrors.lastname = 'Please Enter Last Name !';
      validationErrors.errors = true;
    }

    if (!settings?.email?.trim()) {
      validationErrors.email = 'Please Enter Email !';
      validationErrors.errors = true;
    } else if (!EMAIL_REGEX.test(settings.email.toLowerCase())) {
      validationErrors.email = 'Please Enter Valid Email !';
      validationErrors.errors = true;
    }

    const phone: string = settings?.phone || '';

    if (phone.length < 10) {
      validationErrors.phone = 'Please Enter a 10 digit Phone Number';
      validationErrors.errors = true;
    } else if (phone.length > 20) {
      validationErrors.phone = 'Phone Number can not be more than 20 digits';
      validationErrors.errors = true;
    } else if (!phone.match(PHONE_REGEX)) {
      validationErrors.phone = 'Please Enter a Valid Phone Number';
      validationErrors.errors = true;
    }

    return validationErrors;
  };

  const syncGlobalReduxUserData = (updatedFields: Record<string, any>) => {
    const updatedUser = {
      ...user,
      ...updatedFields,
      phone: formatPhoneNumber(updatedFields.phone || ''),
    };

    dispatch(updateAuthenticatedUser(updatedUser));
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!settings) return false;
    const validationErrors: ValidationErrors = validateForm();

    setErrors(validationErrors);
    setSaveClicked(true);

    if (validationErrors.errors) return false;

    setIsLoading(true);

    try {
      let imageUrl: any = null;

      if (
        settings.selectedProfileImage &&
        settings.selectedProfileImage.length > 0
      ) {
        try {
          imageUrl = await upload(settings.selectedProfileImage[0]);

          if (!imageUrl) {
            throw new Error('Upload returned empty URL');
          }
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          toast.error(
            '🚫 Profile image upload failed. Please try a different image or check your connection.'
          );
          setIsLoading(false);
          setSaveClicked(false);
          return false;
        }
      }

      const params: Record<string, any> = {
        id: settings.id,
        email: settings.email,
        username: settings.email,
        firstname: settings.firstname,
        lastname: settings.lastname,
        phone: formatPhoneNumber(settings.phone || ''),
        profileImage: imageUrl ?? settings.profileImage,
        CreatedBy: user.id,
        RoleName: userRoleName,
        RoleId: user.roles[0].role.id,
        calendlyAPIKey: settings.calendlyAPIKey,
      };

      if (showCalendarFields) {
        params.calendarLink = settings.calendarLink?.trim() ?? '';
      }

      const response: any = await updateUserProfile(params);

      if (response.ok || response.status === 200) {
        toast.success('ⓘ User profile has been updated.', {
          autoClose: 10000,
          position: 'top-center',
        });

        const persistedSettings: UserProfileSettings = {
          ...settings,
          phone: params.phone,
          profileImage: params.profileImage,
          selectedProfileImage: [],
        };

        setSettings(persistedSettings);
        setSavedSettings(persistedSettings);

        syncGlobalReduxUserData(params);
        setTimeout(clearSaveState, 2000);
        return true;
      } else if (response.status === 403) {
        toast.error(' 🚫 You have provided invalid calendly API key !');
      } else if (response.status === 409) {
        toast.error(' 🚫 Calendly API key with this url already exists !');
      } else {
        toast.error('🚫 Failed to update profile details. Please try again.');
      }

      return false;
    } catch (error) {
      console.error('Update process failed', error);
      toast.error('Something went wrong. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Unlink = persist blanked Calendly fields via the same profile-update endpoint (there's no
  // dedicated unlink route). Builds params from a cleared copy rather than relying on updateField
  // + handleSubmit, which would save against a stale settings closure.
  const unlinkCalendly = async (): Promise<boolean> => {
    if (!settings) {
      return false;
    }

    setIsLoading(true);

    try {
      const params: Record<string, any> = {
        id: settings.id,
        email: settings.email,
        username: settings.email,
        firstname: settings.firstname,
        lastname: settings.lastname,
        phone: formatPhoneNumber(settings.phone || ''),
        profileImage: settings.profileImage,
        CreatedBy: user.id,
        RoleName: userRoleName,
        RoleId: user.roles[0].role.id,
        calendlyAPIKey: '',
        calendarLink: '',
      };

      const response: any = await updateUserProfile(params);

      if (response.ok || response.status === 200) {
        setSettings((prev: UserProfileSettings) =>
          prev ? { ...prev, calendlyAPIKey: '', calendarLink: '' } : prev
        );
        toast.success('ⓘ Calendly has been unlinked.', {
          autoClose: 8000,
          position: 'top-center',
        });
        return true;
      }

      toast.error('🚫 Failed to unlink Calendly. Please try again.');
      return false;
    } catch (error) {
      console.error('Calendly unlink failed', error);
      toast.error('Something went wrong unlinking Calendly. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    settings,
    errors,
    saveClicked,
    calendlyAPIKeyHidden,
    showCalendarFields,
    user,
    isLoading,
    isDirty,
    stagedPreviewUrl,
    hasStagedImage,
    updateField,
    handleSubmit,
    unlinkCalendly,
    clearSaveState,
    toggleCalendlyKeyVisibility,
  };
};
